import type { Member } from "src/types/matchControl/member";

/**
 * THE source of truth for line-up member columns and their order. Both the row
 * editor (`MemberRow`) and the spreadsheet paste parser (`parseLineUpPaste`)
 * derive their column order from the active preset, so the two can never drift
 * apart. Reorder here to reorder everywhere.
 *
 * Presets are hardcoded on purpose: the line-up dialog is expected to be
 * deprecated once Data Resources are supported (MXMZ-2265).
 */

export type MemberColumnKey = Exclude<keyof Member, "id">;

export type MemberColumn = {
	key: MemberColumnKey;
	label: string;
	type: "text" | "number" | "select" | "url";
	required: boolean;
	placeholder?: string;
	/** Column width relative to the other columns; must be a literal Tailwind class. */
	className: string;
};

const PRESET_1_COLUMNS = [
	{
		key: "number",
		label: "Number",
		type: "text",
		required: true,
		placeholder: "Member Number",
		className: "flex-1",
	},
	{
		key: "name",
		label: "Name",
		type: "text",
		required: true,
		placeholder: "Member Name",
		className: "flex-[2.5]",
	},
	{ key: "type", label: "Type", type: "select", required: true, className: "flex-1" },
	{ key: "goals", label: "Goals", type: "number", required: false, className: "flex-1" },
	{ key: "caps", label: "Caps", type: "number", required: false, className: "flex-1" },
	{ key: "matches", label: "Matches", type: "number", required: false, className: "flex-1" },
	{ key: "age", label: "Age", type: "number", required: false, className: "flex-1" },
] as const satisfies readonly MemberColumn[];

// Requested for a hockey rundown, but nothing in it is hockey specific.
// Every column is optional — nothing blocks saving a line-up.
const PRESET_2_COLUMNS = [
	{
		key: "number",
		label: "Number",
		type: "text",
		required: false,
		placeholder: "Shirt Number",
		className: "flex-1",
	},
	{
		key: "name",
		label: "Name",
		type: "text",
		required: false,
		placeholder: "Member Name",
		className: "flex-[2]",
	},
	{ key: "type", label: "Type", type: "select", required: false, className: "flex-1" },
	{
		key: "photoUrl",
		label: "Photo URL",
		type: "url",
		required: false,
		placeholder: "https://…",
		className: "flex-[2]",
	},
	{ key: "caps", label: "Caps", type: "number", required: false, className: "flex-1" },
	{ key: "goals", label: "Goals", type: "number", required: false, className: "flex-1" },
	{
		key: "goalsTournament",
		label: "Goals this tournament",
		type: "number",
		required: false,
		placeholder: "Goals (t)",
		className: "flex-1",
	},
] as const satisfies readonly MemberColumn[];

export type LineUpPreset = "preset1" | "preset2";

type LineUpPresetDefinition = {
	label: string;
	columns: readonly MemberColumn[];
	/** Example data rows shown in the paste helper, tab separated per column. */
	pasteExample: readonly string[];
};

export const LINE_UP_PRESETS = {
	preset1: {
		label: "Preset 1",
		columns: PRESET_1_COLUMNS,
		pasteExample: ["7\tAlice Foster\tstarter\t0\t50\t4\t29", "8\tBar Bench\tbench\t2\t10\t9\t22"],
	},
	preset2: {
		label: "Preset 2",
		columns: PRESET_2_COLUMNS,
		pasteExample: [
			"7\tAlice Foster\tstarter\thttps://cdn.example.com/alice.png\t50\t12\t3",
			"8\tBar Bench\tbench\thttps://cdn.example.com/bar.png\t10\t2\t0",
		],
	},
} as const satisfies Record<LineUpPreset, LineUpPresetDefinition>;

export const DEFAULT_LINE_UP_PRESET: LineUpPreset = "preset1";

export const LINE_UP_PRESET_OPTIONS = (Object.keys(LINE_UP_PRESETS) as LineUpPreset[]).map(
	(preset) => ({ label: LINE_UP_PRESETS[preset].label, value: preset }),
);

export const isLineUpPreset = (value: unknown): value is LineUpPreset =>
	typeof value === "string" && value in LINE_UP_PRESETS;

type AnyMemberColumn = (typeof PRESET_1_COLUMNS | typeof PRESET_2_COLUMNS)[number];

export type NumericMemberColumnKey = Extract<AnyMemberColumn, { type: "number" }>["key"];

export type TextMemberColumnKey = Extract<AnyMemberColumn, { type: "text" | "url" }>["key"];

// Fail the build if a new editable `Member` field is added without listing it in
// a preset. (`satisfies` above already rejects unknown/misshapen columns, and a
// mislabeled `type` is caught where the value is assigned.)
const assertColumnsCoverMember = <_T extends true>(): void => {};
assertColumnsCoverMember<MemberColumnKey extends AnyMemberColumn["key"] ? true : false>();
